import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  getSunoPrompt,
  getSunoPrompts,
  type SunoPrompt
} from '../../../api/suno'
import {
  promptToBaseSnapshot,
  type SelectedBasePrompt
} from './stylePromptGeneratorTypes'

type UseStylePromptLibraryOptions = {
  onBasePromptLoadError: (message: string) => void
  onBasePromptLoaded: () => void
}

export const useStylePromptLibrary = ({
  onBasePromptLoadError,
  onBasePromptLoaded
}: UseStylePromptLibraryOptions) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const basePromptId = searchParams.get('basePromptId')

  const [selectedBasePrompt, setSelectedBasePrompt] = useState<SelectedBasePrompt | null>(null)
  const [promptLibraryOpen, setPromptLibraryOpen] = useState(false)
  const [promptLibrarySearch, setPromptLibrarySearch] = useState('')
  const [promptLibraryPrompts, setPromptLibraryPrompts] = useState<SunoPrompt[]>([])
  const [promptLibraryLoading, setPromptLibraryLoading] = useState(false)
  const [promptLibraryError, setPromptLibraryError] = useState<string | null>(null)

  const loadPromptLibrary = useCallback(async (search = promptLibrarySearch): Promise<void> => {
    setPromptLibraryLoading(true)
    setPromptLibraryError(null)

    try {
      const result = await getSunoPrompts({ search, pageSize: 100 })
      setPromptLibraryPrompts(result.items)
    } catch (requestError) {
      setPromptLibraryError(requestError instanceof Error ? requestError.message : 'Unable to load prompt library.')
    } finally {
      setPromptLibraryLoading(false)
    }
  }, [promptLibrarySearch])

  const openPromptLibrary = useCallback((): void => {
    setPromptLibraryOpen(true)
    void loadPromptLibrary(promptLibrarySearch)
  }, [loadPromptLibrary, promptLibrarySearch])

  const clearSelectedBasePrompt = useCallback((): void => {
    setSelectedBasePrompt(null)

    if (basePromptId) {
      const nextSearchParams = new URLSearchParams(searchParams)
      nextSearchParams.delete('basePromptId')
      setSearchParams(nextSearchParams, { replace: true })
    }
  }, [basePromptId, searchParams, setSearchParams])

  const selectPromptLibraryPrompt = (prompt: SunoPrompt): void => {
    setSelectedBasePrompt({ ...promptToBaseSnapshot(prompt), source: 'library' })
    setPromptLibraryOpen(false)
  }

  useEffect(() => {
    if (!basePromptId || selectedBasePrompt?.id === basePromptId) {
      return
    }

    let ignore = false

    const loadSelectedBasePrompt = async (): Promise<void> => {
      try {
        const prompt = await getSunoPrompt(basePromptId)

        if (!ignore) {
          setSelectedBasePrompt({ ...promptToBaseSnapshot(prompt), source: 'library' })
          onBasePromptLoaded()
        }
      } catch (requestError) {
        if (!ignore) {
          setSelectedBasePrompt(null)
          onBasePromptLoadError(requestError instanceof Error ? requestError.message : 'Unable to load selected base prompt.')
        }
      }
    }

    void loadSelectedBasePrompt()

    return () => {
      ignore = true
    }
  }, [basePromptId, onBasePromptLoadError, onBasePromptLoaded, selectedBasePrompt?.id])

  return {
    selectedBasePrompt,
    promptLibraryError,
    promptLibraryLoading,
    promptLibraryOpen,
    promptLibraryPrompts,
    promptLibrarySearch,
    clearSelectedBasePrompt,
    loadPromptLibrary,
    openPromptLibrary,
    selectPromptLibraryPrompt,
    setPromptLibraryOpen,
    setPromptLibrarySearch,
    setSelectedBasePrompt
  }
}
